import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import {
  useAuthentication,
  useCreateChannelConfiguration,
  useDeleteChannelConfiguration,
  useGetAuthenticatedUser,
  useUpdateChannelConfiguration,
} from '@connect-x-app/hybrid-state';
import { AdminConfigPermissions, ChannelConfiguration, RolesPermissions } from '@connect-x-app/shared-domain-models';
import { ADMIN_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Spinner, toastError, toastSuccess } from '@connect-x-app/web-ui';
import { Row } from '@tanstack/react-table';

import { AddButton, EmptyState, TableLayout } from '../../../../shared';
import { ConfirmationModal } from '../../../../shared/confirmationModal/ConfirmationModal';
import { customModalStyles } from '../../../../shared/modal/ModalUtils';
import { useGetRoles } from '../../../roles/queries';
import { EditChannelConfigurationModal } from './components/EditChannelConfigurationModal';
import { useChannelConfigurationTable } from './hooks/useChannelConfigurationTable';

import './ChannelConfigurations.scss';

export const ChannelConfigurations = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthentication();
  // isFetching stays true while the /me call is in-flight (including background refetches).
  // On page load with cached auth data the Zustand store may still hold stale permissions
  // (e.g. VIEW_ROLES that was removed server-side). Firing the roles fetch against those stale
  // permissions returns a 403; the axios interceptor recovers by refetching auth/me, but waiting
  // here avoids sending a request we already know is doomed.
  // Sharing the query key with AppAdmin means no extra network request.
  const { isFetching: isAuthFetching } = useGetAuthenticatedUser(true);
  const canViewRoles = useMemo(() => hasPermission(RolesPermissions.VIEW), [hasPermission]);
  const { data: roles } = useGetRoles({ enabled: !isAuthFetching && canViewRoles });

  const [channelConfigToEdit, setChannelConfigToEdit] = useState<ChannelConfiguration>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const canAddOrUpdate = useMemo(() => {
    return hasPermission(AdminConfigPermissions.WRITE);
  }, [hasPermission]);

  const canDelete = useMemo(() => {
    return hasPermission(AdminConfigPermissions.DELETE);
  }, [hasPermission]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, selectedItems, table } = useChannelConfigurationTable({
    canViewRoles,
    renderCheckbox: canDelete,
    roles: roles ?? [],
  });

  const closeModal = () => {
    setIsEditModalOpen(false);
    setChannelConfigToEdit(undefined);
  };

  const { isPending: isCreating, mutate: createChannel } = useCreateChannelConfiguration({
    onError: () => toastError(t('TOAST_ERROR')),
    onSuccess: () => {
      toastSuccess(t('TOAST_SUCCESS'));
      closeModal();
    },
  });

  const { isPending: isUpdating, mutate: updateChannel } = useUpdateChannelConfiguration({
    onError: () => toastError(t('TOAST_ERROR')),
    onSuccess: () => {
      toastSuccess(t('TOAST_SUCCESS'));
      closeModal();
    },
  });

  const { deleteSelectedChannels, isPending: isDeleting } = useDeleteChannelConfiguration();

  const onDeleteChannels = async () => {
    try {
      await deleteSelectedChannels(selectedItems);
      toastSuccess(t('TOAST_SUCCESS'));
      table.resetRowSelection();
      setIsDeleteModalOpen(false);
    } catch {
      toastError(t('TOAST_ERROR'));
    }
  };

  const onRowClicked = (row: Row<ChannelConfiguration>) => {
    setChannelConfigToEdit(row.original);
    setIsEditModalOpen(true);
  };

  const renderEmptyStateButton = useCallback(() => {
    if (!canAddOrUpdate) {
      return null;
    }

    return <AddButton label={t('CHANNEL_CONFIG.BUTTONS.ADD')} onClick={() => setIsEditModalOpen(true)} />;
  }, [canAddOrUpdate, t]);

  const renderEmptyState = useCallback(() => {
    return (
      <EmptyState
        button={renderEmptyStateButton}
        description={
          canAddOrUpdate ? t('CHANNEL_CONFIG.EMPTY_STATE.DESCRIPTION') : t('CHANNEL_CONFIG.EMPTY_STATE.DESCRIPTION_NO_WRITE')
        }
        title={t('CHANNEL_CONFIG.EMPTY_STATE.TITLE')}
      />
    );
  }, [canAddOrUpdate, renderEmptyStateButton, t]);

  if (isLoading) {
    return <Spinner />;
  }

  const onModalSubmit = (channel: ChannelConfiguration) => {
    if (channel.id) {
      updateChannel(channel);
    } else {
      createChannel(channel);
    }
  };

  return (
    <>
      <div data-test={ADMIN_TEST_IDS.channelConfig.table}>
        <TableLayout
          addButtonAction={() => setIsEditModalOpen(true)}
          addButtonLabel={t('CHANNEL_CONFIG.BUTTONS.ADD')}
          deleteButtonAction={() => setIsDeleteModalOpen(true)}
          deleteButtonDisabled={!selectedItems.length}
          deleteButtonLabel={t('SHARED.BUTTONS.ARCHIVE')}
          emptyState={renderEmptyState}
          isAddButtonPresent={canAddOrUpdate}
          isDeleteButtonPresent={canDelete}
          onEndReached={() => {
            if (hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          onRowClicked={onRowClicked}
          rowClickAllowed={canAddOrUpdate}
          table={table}
        />
        {isFetchingNextPage && (
          <div className="spinner-wrapper">
            <Spinner />
          </div>
        )}
      </div>
      <Modal isOpen={isEditModalOpen || isDeleteModalOpen} style={customModalStyles}>
        {isEditModalOpen ? (
          <EditChannelConfigurationModal
            canViewRoles={canViewRoles}
            channel={channelConfigToEdit}
            channels={data}
            closeModal={closeModal}
            isLoading={isCreating || isUpdating}
            onSubmit={onModalSubmit}
            roles={roles ?? []}
          />
        ) : null}
        {isDeleteModalOpen ? (
          <ConfirmationModal
            cancelButtonAction={() => setIsDeleteModalOpen(false)}
            cancelButtonLabel={t('SHARED.BUTTONS.CANCEL')}
            confirmButtonAction={onDeleteChannels}
            confirmButtonLabel={t('SHARED.BUTTONS.ARCHIVE')}
            loading={isDeleting}
            title={`${t('SHARED.BUTTONS.ARCHIVE')} ${t('CHANNEL_CONFIG.LABEL', { count: selectedItems.length })}`}
          />
        ) : null}
      </Modal>
    </>
  );
};
